import React, { useState } from 'react';
import { ChatInterface } from '../../components/shared/ChatInterface';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Sparkles, Users, ChevronRight } from 'lucide-react';

const recentPatients = [
  { id: 1, name: 'Mei Ling Wong', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100', condition: 'Post-Surgery' },
  { id: 2, name: 'Ahmad Ismail', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100', condition: 'Lower Back Pain' },
  { id: 3, name: 'Sarah Lee', avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100', condition: 'ACL Recovery' },
];

const aiResponses = {
  "Summarize this patient's progress": "Based on the data for Mei Ling Wong:\n\n• **Current Recovery Score:** 68/100 (baseline: 32)\n• **Sessions Completed:** 12 of 20\n• **Compliance Rate:** 58% (below target)\n\n**Key Observations:**\n- Progress has slowed in the past 2 weeks\n- Pain scores have improved from 8 to 3\n- Missing exercises on alternate days\n\n**Recommendation:** Consider adjusting exercise intensity and scheduling a motivational check-in.",
  
  "Create a supportive follow-up message": "Here's a supportive message for Mei Ling:\n\n---\n\nHi Mei Ling! 👋\n\nI wanted to check in and see how you're feeling this week. I've noticed your pain scores have improved significantly - that's wonderful progress!\n\nI understand it can be challenging to stay consistent with the exercises, especially when life gets busy. Remember, every small step counts toward your recovery.\n\nWould you like to schedule a quick call to discuss any concerns or adjust your plan? I'm here to support you!\n\nWarm regards,\nDr. Chen\n\n---\n\n*Feel free to modify this message before sending.*",
  
  "Suggest a 2-week progression plan": "**2-Week Progression Plan for Mei Ling Wong**\n\n**Week 1: Foundation & Consistency**\n- Reduce resistance by 20%\n- Focus on form and range of motion\n- 3 sessions/week (Mon, Wed, Fri)\n- Daily 10-min mobility routine\n\n**Week 2: Gradual Increase**\n- Increase resistance by 10% from reduced level\n- Add balance exercises (5 min each session)\n- Maintain 3 sessions/week\n- Introduce stair climbing practice\n\n**Success Metrics:**\n- Compliance target: 80%+\n- Pain score: Maintain ≤4\n- ROM improvement: +5 degrees flexion\n\n*Note: Adjust based on patient feedback and tolerance.*",
  
  "What might be causing plateau?": "**Analysis of Mei Ling's Progress Plateau**\n\n**Possible Contributing Factors:**\n\n1. **Exercise Intensity Mismatch**\n   - Current program may be too challenging\n   - Leading to avoidance and missed sessions\n\n2. **Recovery Time**\n   - Post-surgery patients often hit plateaus at 6-8 weeks\n   - Body adapting to new movement patterns\n\n3. **Compliance Pattern**\n   - 58% compliance is below optimal threshold\n   - Inconsistent practice prevents muscle memory formation\n\n4. **Psychological Factors**\n   - Fear of re-injury common at this stage\n   - May be unconsciously limiting effort\n\n**Recommended Actions:**\n- Schedule assessment to check ROM and strength\n- Consider reducing intensity temporarily\n- Add variety to prevent exercise fatigue\n- Discuss concerns about fear of movement"
};

export default function TherapistChat() {
  const [messages, setMessages] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(recentPatients[0]);

  const handleSendMessage = (content) => {
    // Add user message
    setMessages(prev => [...prev, { role: 'user', content }]);
    
    // Simulate AI response
    setTimeout(() => {
      const response = aiResponses[content] || 
        `I understand you're asking about "${content}". Let me analyze the available data for ${selectedPatient.name} and provide insights.\n\nBased on the patient's history and current status, I recommend reviewing their recent compliance patterns and considering a follow-up assessment.\n\n*This is an AI-generated suggestion. Please verify with clinical judgment.*`;
      
      setMessages(prev => [...prev, { role: 'assistant', content: response }]);
    }, 1500);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-foreground">AI Copilot Assistant</h1>
        <p className="text-sm text-muted-foreground mt-1">Get AI-powered insights and assistance for your patients</p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Patient Context Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Users className="w-4 h-4" />
                Patient Context
              </CardTitle>
              <CardDescription className="text-xs">Select a patient for contextual AI assistance</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {recentPatients.map((patient) => (
                <div
                  key={patient.id}
                  className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
                    selectedPatient?.id === patient.id 
                      ? 'bg-primary-light border border-primary/20' 
                      : 'hover:bg-muted'
                  }`}
                  onClick={() => setSelectedPatient(patient)}
                >
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={patient.avatar} />
                    <AvatarFallback>{patient.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{patient.name}</p>
                    <p className="text-xs text-muted-foreground">{patient.condition}</p>
                  </div>
                  {selectedPatient?.id === patient.id && (
                    <Badge variant="secondary" className="text-[10px]">Active</Badge>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
          
          {/* Quick Actions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {[
                'Generate progress report',
                'Draft care plan update',
                'Analyze compliance trends',
                'Compare with similar cases'
              ].map((action, idx) => (
                <Button
                  key={idx}
                  variant="ghost"
                  className="w-full justify-start text-sm h-9"
                  onClick={() => handleSendMessage(action)}
                >
                  <ChevronRight className="w-3 h-3 mr-2 text-primary" />
                  {action}
                </Button>
              ))}
            </CardContent>
          </Card>
        </div>
        
        {/* Chat Interface */}
        <div className="lg:col-span-3 h-[calc(100vh-220px)] min-h-[500px]">
          <ChatInterface
            messages={messages}
            onSendMessage={handleSendMessage}
            placeholder={`Ask about ${selectedPatient?.name || 'your patient'}...`}
            showQuickPrompts={messages.length === 0}
            aiName="AI Copilot"
            userAvatar="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=100"
            disclaimer="Assistive suggestion — clinician decides"
          />
        </div>
      </div>
    </div>
  );
}
